import { FC, useMemo, useState } from "react";
import colors from "tailwindcss/colors";
import { useListOrganizationCategories } from "~/api/organization/organization";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useOrganizationParams } from "~/helpers";
import { cn } from "~/lib/utils";

type ColorName = keyof typeof colors;
type ColorShade = "300" | "400" | "500" | "600" | "700" | "800" | "900";

const COLOR_NAMES: ColorName[] = [
  "slate",
  "gray",
  "zinc",
  "neutral",
  "stone",
  "red",
  "orange",
  "amber",
  "yellow",
  "lime",
  "green",
  "emerald",
  "teal",
  "cyan",
  "sky",
  "blue",
  "indigo",
  "violet",
  "purple",
  "fuchsia",
  "pink",
  "rose",
];

const COLOR_SHADES: ColorShade[] = [
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
];

const ALL_COLORS = COLOR_NAMES.flatMap((name) =>
  COLOR_SHADES.map((shade) => colors[name][shade])
);

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  categoryId?: number; // Optional category ID to exclude from the check
}

export const ColorPicker: FC<ColorPickerProps> = ({
  value,
  onChange,
  categoryId: _categoryId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(value);
  const { organizationId } = useOrganizationParams();
  const { data: categories = [] } =
    useListOrganizationCategories(organizationId);

  // Get all colors that are already in use by other categories
  const usedColors = categories.map((category) => category.color.toLowerCase());

  const handleColorChange = (color: string) => {
    try {
      setSelectedColor(color);
      onChange(color);
    } catch (error) {
      setSelectedColor(value);
    } finally {
      setIsOpen(false);
    }
  };

  const availableColors = useMemo(() => {
    const selectedLower = selectedColor.toLowerCase();
    return ALL_COLORS.filter((hex) => {
      const lower = hex.toLowerCase();
      return !usedColors.includes(lower) && lower !== selectedLower;
    });
  }, [usedColors, selectedColor]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "h-6 w-6 rounded-full border border-input",
            "ring-offset-background transition-all hover:scale-110",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-ring focus-visible:ring-offset-2"
          )}
          style={{ backgroundColor: selectedColor }}
        />
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3">
        {availableColors.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            All colors are currently used.
          </div>
        ) : (
          <div className="grid h-40 grid-cols-6 gap-2 overflow-y-scroll">
            {availableColors.map((hex, index) => (
              <button
                key={`${hex}-${index}`}
                className={cn(
                  "h-6 w-6 rounded-full border border-input",
                  "ring-offset-background transition-all hover:scale-110",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-ring focus-visible:ring-offset-2"
                )}
                style={{ backgroundColor: hex }}
                onClick={() => handleColorChange(hex)}
                aria-label={`Select color ${hex}`}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
export default ColorPicker;
